import { useRef } from 'react';

/**
 * Bottom-edge swipe-up gesture (mobile). Returns touch handlers to spread onto
 * a container; fires `onSwipeUp` when a touch that STARTS near the bottom edge
 * of the viewport travels upward past a threshold.
 *
 * Restores Jim's original "swipe up to open the site detail" gesture (#267)
 * without fighting normal content scrolling — the bottom-edge start makes it a
 * deliberate gesture, and touch events never fire on desktop (the visible
 * "Site Detail" button stays the desktop affordance).
 */
export function useSwipeUp(
  onSwipeUp: (() => void) | undefined,
  opts: { edgePx?: number; thresholdPx?: number } = {}
) {
  const edgePx = opts.edgePx ?? 60; // touch must start within this many px of the bottom
  const thresholdPx = opts.thresholdPx ?? 60; // must travel up at least this far
  const start = useRef<{ x: number; y: number; fromBottom: boolean } | null>(
    null
  );

  if (!onSwipeUp) return {};

  return {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const fromBottom = window.innerHeight - t.clientY <= edgePx;
      start.current = { x: t.clientX, y: t.clientY, fromBottom };
    },
    onTouchEnd: (e: React.TouchEvent) => {
      const s = start.current;
      start.current = null;
      if (!s || !s.fromBottom) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dy = t.clientY - s.y; // negative = upward
      const dx = Math.abs(t.clientX - s.x);
      // Clear upward motion that's more vertical than horizontal.
      if (dy < -thresholdPx && dx < Math.abs(dy)) {
        onSwipeUp();
      }
    },
  };
}
